import { datepickerToISOFormat } from '@yes.technology/react-toolkit'
import { DEFAULT_MIN_MAX_YEARS_FROM_TODAY } from '../modules/shared/Constants'

export type IsValidDateIntervalProps = {
  dateTime: string
  minDate?: string
  maxDate?: string
  lang: AllowedLocales
}

// TODO: import type from toolkit when available
type DatepickerToISOFormatOptions = Parameters<typeof datepickerToISOFormat>[1]

type ExtractLocale<T> = T extends { locale?: infer Locale } ? Locale : never

export type AllowedLocales = Extract<
  ExtractLocale<DatepickerToISOFormatOptions>,
  string
>

const TIME_REGEX: Record<AllowedLocales, RegExp> = {
  'de-DE': /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/, // e.g.: 00:05:59
  'en-US': /^(1[012]|0?[1-9]):[0-5][0-9]:[0-5][0-9] [AP]M$/, // e.g.: 12:05:59 AM
  'es-419': /^(1[012]|0?[1-9]):[0-5][0-9]:[0-5][0-9] [ap]\.m\.$/, // e.g.: 12:05:59 a.m.
  'pt-BR': /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/ // e.g.: 00:05:59
}

const DATE_REGEX: Record<AllowedLocales, RegExp> = {
  'de-DE': /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[012])\.\d{4}$/, // e.g.: 29.01.2025
  'en-US': /^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/, // e.g.: 01/29/2025
  'es-419': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/, // 29/01/2025
  'pt-BR': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/ // e.g.: 29/01/2025
}

type IsValidDateTimeProps = {
  dateTime: string
  minYear?: number
  maxYear?: number
  lang: AllowedLocales
  contentType?: string
}

/**
 *  date format:      'dd/mm/YYYY' (according to locale)
 *  time format:      'hh:mm:ss' (according to locale)
 *  dateTime formant: '<date> | <time>'
 *
 *  contentType:      'date' or 'datetime'
 *  lang:             'en-US', 'pt-BR' etc.
 */
export const isValidDateTime = ({
  dateTime,
  minYear,
  maxYear,
  lang,
  contentType = 'date'
}: IsValidDateTimeProps) => {
  const isValidDate = (dateOnly: string) => {
    if (!DATE_REGEX[lang].test(dateOnly)) {
      return false
    }

    const year = parseInt(dateOnly.slice(-4))
    const yearNow = new Date().getFullYear()

    const isYearInsideRange =
      year >= (minYear || yearNow - DEFAULT_MIN_MAX_YEARS_FROM_TODAY) &&
      year <= (maxYear || yearNow + DEFAULT_MIN_MAX_YEARS_FROM_TODAY)

    return isYearInsideRange
  }

  const isValidTime = (time: string) => {
    return TIME_REGEX[lang].test(time)
  }

  if (contentType === 'date') {
    return isValidDate(dateTime)
  }

  const [date, time] = dateTime.split(' | ')
  return isValidDate(date) && time && isValidTime(time)
}

/**
 *  date format:      'dd/mm/aaaa' or 'dd-mm-aaaa'
 *  date format US:   'mm/dd/yyyy' or 'mm-dd-aaaa'
 *  time format:      'hh:mm:ss'
 *  dateTime formant: '<date> | <time>' (aka DatePickerFormat)
 *
 *  (min|max)Date: ISO format Eg. YYYY-MM-DDThh:mm:ss or YYYY-MM-DD
 *
 *  contentType:      'date' or 'datetime'
 *  lang:             'en-US' or 'pt-BR' etc.
 */
export const isValidDateInterval = ({
  dateTime,
  minDate,
  maxDate,
  lang
}: IsValidDateIntervalProps): boolean => {
  const isoFormattedDate = datepickerToISOFormat(dateTime, { locale: lang })
  const date = new Date(isoFormattedDate || '')

  const min = minDate ? new Date(minDate) : null
  const max = maxDate ? new Date(maxDate) : null

  const isAfterMinDate = min ? date >= min : true
  const isBeforeMaxDate = max ? date <= max : true

  return isAfterMinDate && isBeforeMaxDate
}
